import { ImportHTMLProps } from '@custom/Utils/constants/interfaces';
import React, { Fragment, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';

const ImportHtml = forwardRef(({ isEditable, html, onInputChange }: ImportHTMLProps, ref) => {
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  // Function to capture form data from input elements
  const captureFormData = () => {
    if (htmlContainerRef.current) {
      const formElements = htmlContainerRef.current.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input, textarea, select'
      );
      const data: { [key: string]: any } = {};

      formElements.forEach((element) => {
        const { type, name, value, checked, id } = element as any;

        if (type === 'radio' && checked) {
          data[name] = value;
        } else if (type === 'checkbox' && checked) {
          if (!Array.isArray(data[name])) {
            data[name] = [];
          }
          (data[name] as string[]).push(value);
        } else if (element.tagName.toLowerCase() === 'select') {
          const selectElement = element as HTMLSelectElement;
          if (selectElement.multiple) {
            const selectedOptions = Array.from(selectElement.selectedOptions).map(option => option.value);
            data[id] = selectedOptions;
          } else {
            data[id] = selectElement.value;
          }
        } else {
          data[id] = value;
        }
      });

      return data;
    }
    return {};
  };

  // Function to update the inner HTML with form data
  const updateHtmlContent = () => {
    if (htmlContainerRef.current) {
      const formElements = htmlContainerRef.current.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input, textarea, select'
      );

      formElements.forEach((element) => {
        const { type, tagName, id } = element;
        const inputElement = element as HTMLInputElement;

        if (type === 'radio' || type === 'checkbox') {
          // Set the checked property directly
          const isChecked = inputElement.checked;
          if (isChecked) {
            element.setAttribute('checked', '');
          } else {
            element.removeAttribute('checked');
          }

        } else if (tagName.toLowerCase() === 'select') {
          const selectElement = element as HTMLSelectElement;
          Array.from(selectElement.options).forEach(option => {
            option.setAttribute('selected', selectElement.value === option.value ? 'selected' : '');
          });
        } else if (tagName.toLowerCase() === 'textarea') {
          (element as HTMLTextAreaElement).textContent = (element as HTMLTextAreaElement).value;
        } else {
          element.setAttribute('value', inputElement.value);
        }
      });

      // Return the updated HTML with the values
      const updatedHtml = htmlContainerRef.current.innerHTML;
      return updatedHtml;
    }
  };

  // Expose the `fetchInfo` function to the parent
  useImperativeHandle(ref, () => ({
    fetchInfo,
  }));

  // Function to handle form submission
  const fetchInfo = () => {
    const json = captureFormData();
    const updatedHtml = updateHtmlContent();
    return { json, html: updatedHtml };
  };

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    // Update the content inside the textarea or other elements
    if (target.tagName.toLowerCase() === 'textarea') {
      // Set the text content for textarea directly
      target.textContent = target.value;
    } else if (target.tagName.toLowerCase() === 'input') {
      target.setAttribute('value', target.value);
    } else if (target.tagName.toLowerCase() === 'select') {
      Array.from((target as HTMLSelectElement).options).forEach(option => {
        option.setAttribute('selected', option.selected ? 'selected' : '');
      });
    }
    if (onInputChange) {
      onInputChange(e);
    }
  };

  useEffect(() => {
    const container = htmlContainerRef.current;

    if (container) {
      // Find all form elements and attach event listeners for input or change events
      const formElements = container.querySelectorAll('input, textarea, select');
      formElements.forEach((element) => {
        element.addEventListener('input', handleInputChange);
        element.addEventListener('change', handleInputChange);
      });

      // Clean up the event listeners when the component is unmounted
      return () => {
        formElements.forEach((element) => {
          element.removeEventListener('input', handleInputChange);
          element.removeEventListener('change', handleInputChange);
        });
      };
    }
  }, [html]);


  // Enforce read-only behavior for imported HTML when isEditable is false
  useEffect(() => {
    const container = htmlContainerRef.current;
    if (!container) return;

    const formElements = Array.from(
      container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select')
    );
    const editableNodes = Array.from(
      container.querySelectorAll<HTMLElement>('[contenteditable="true"], [contentEditable="true"]')
    );

    // Handlers to block user-initiated edits while allowing scroll
    const blockEditEvent = (e: Event) => {
      const type = e.type;
      if (type === 'keydown' || type === 'paste' || type === 'drop' || type === 'cut') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const blockChange = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (!isEditable) {
      // Disable nested contenteditable nodes
      editableNodes.forEach(node => {
        node.setAttribute('contenteditable', 'false');
        (node as any).contentEditable = 'false';
      });

      formElements.forEach(el => {
        const tag = el.tagName.toLowerCase();
        // For inputs/textareas, prefer readOnly to avoid visual disabled state
        if (tag === 'input' || tag === 'textarea') {
          try { (el as HTMLInputElement | HTMLTextAreaElement).readOnly = true; 
          } catch { }
        }
        // For select, prevent interaction via events to avoid greyed UI
        if (tag === 'select') {
          el.addEventListener('mousedown', blockChange, true);
          el.addEventListener('click', blockChange, true);
          el.addEventListener('change', blockChange, true);
          el.addEventListener('keydown', blockEditEvent, true);

        }
        // Block edit-intent events for all controls
        el.addEventListener('keydown', blockEditEvent, true);
        el.addEventListener('paste', blockEditEvent, true);
        el.addEventListener('drop', blockEditEvent, true);
        el.addEventListener('cut', blockEditEvent, true);
      });

      // Also block on the container to catch stray events
      container.addEventListener('keydown', blockEditEvent, true);
      container.addEventListener('paste', blockEditEvent, true);
      container.addEventListener('drop', blockEditEvent, true);
      container.addEventListener('cut', blockEditEvent, true);

      return () => {
        // Cleanup listeners
        formElements.forEach(el => {
          el.removeEventListener('keydown', blockEditEvent, true);
          el.removeEventListener('paste', blockEditEvent, true);
          el.removeEventListener('drop', blockEditEvent, true);
          el.removeEventListener('cut', blockEditEvent, true);
          if (el.tagName.toLowerCase() === 'select') {
            el.removeEventListener('mousedown', blockChange, true);
            el.removeEventListener('click', blockChange, true);
            el.removeEventListener('change', blockChange, true);
            el.removeEventListener('keydown', blockEditEvent, true);
          }
        });
        container.removeEventListener('keydown', blockEditEvent, true);
        container.removeEventListener('paste', blockEditEvent, true);
        container.removeEventListener('drop', blockEditEvent, true);
        container.removeEventListener('cut', blockEditEvent, true);
      };
    } else {
      // Re-enable inputs/textareas by removing readOnly
      formElements.forEach(el => {
        const tag = el.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea') {
          try { (el as HTMLInputElement | HTMLTextAreaElement).readOnly = false; 

          } catch { }
        }
        // Ensure selects are free of our blockers
        if (tag === 'select') {
          el.removeEventListener('mousedown', blockChange, true);
          el.removeEventListener('click', blockChange, true);
          el.removeEventListener('change', blockChange, true);
          el.removeEventListener('keydown', blockEditEvent, true);
        }
        el.removeEventListener('keydown', blockEditEvent, true);
        el.removeEventListener('paste', blockEditEvent, true);
        el.removeEventListener('drop', blockEditEvent, true);
        el.removeEventListener('cut', blockEditEvent, true);
      });
    }
  }, [isEditable, html]);

  return (
    <Fragment>
      <div
        ref={htmlContainerRef}
        id="html-render"
        contentEditable={isEditable}
        dangerouslySetInnerHTML={{ __html: html || '' }}
      />
    </Fragment>
  );
});

export default ImportHtml;
